import { isTokenExpiredOrExpiringSoon } from './jwtUtils';
import { setToken } from '../features/auth/authSlice';
import { Dispatch } from 'redux';

/**
 * Utility to get a valid token for chat services, refreshing if needed
 * This is specifically for chat advisor services that need to handle long conversations
 */

// Store Auth0's getAccessTokenSilently function reference
let getAccessTokenSilentlyFn: (() => Promise<string>) | null = null;

/**
 * Extra options for chat token refresh.
 * These allow us to support both authenticated users (Auth0)
 * and guest users with separate guest tokens.
 */
export interface ChatTokenRefreshOptions {
    /**
     * Whether the current user is authenticated with Auth0.
     * Defaults to true for backward compatibility.
     */
    isAuthenticated?: boolean;

    /**
     * Callback used to refresh the guest token for unauthenticated users.
     * This should fetch the latest guest token (and typically update it in state)
     * and resolve with the new token string.
     */
    fetchLatestGuestToken?: () => Promise<string | null | undefined>;
}

/**
 * Initialize the token refresh utility with Auth0's getAccessTokenSilently function
 * This should be called once when the app starts
 */
export const initializeChatTokenRefresh = (getAccessTokenSilently: () => Promise<string>) => {
    getAccessTokenSilentlyFn = getAccessTokenSilently;
};

/**
 * Reset the token refresh utility so that Auth0's getAccessTokenSilently
 * will no longer be used (e.g. when the user is logged out or using guest mode)
 */
export const resetChatTokenRefresh = () => {
    getAccessTokenSilentlyFn = null;
};

/**
 * Get a valid token for chat services, refreshing if needed
 * @param currentToken - The current token to check
 * @param bufferSeconds - Buffer time before expiry to trigger refresh (default: 300 = 5 minutes)
 * @param dispatch - Redux dispatch function to update the token in state when refreshed (Auth0 flow)
 * @param options - Extra options for handling authenticated vs guest users
 * @returns Promise<string> - A valid token (current or refreshed)
 */
export const getValidTokenForChat = async (
    currentToken: string,
    bufferSeconds: number = 300,
    dispatch?: Dispatch,
    options?: ChatTokenRefreshOptions
): Promise<string> => {
    // If no current token, we can't refresh
    if (!currentToken) {
        throw new Error('No current token available for refresh');
    }

    // Check if token is expired or expiring soon
    if (isTokenExpiredOrExpiringSoon(currentToken, bufferSeconds)) {
        const isAuthenticated = options?.isAuthenticated ?? true;
        // Authenticated user flow (Auth0)
        if (isAuthenticated) {
            if (!getAccessTokenSilentlyFn) {
                console.warn('Cannot refresh token: getAccessTokenSilently function not initialized');
                // Return current token and let 401 handler deal with it
                return currentToken;
            }

            try {
                const newToken = await getAccessTokenSilentlyFn();

                // Update Redux state with the new token if dispatch is provided
                if (dispatch && newToken !== currentToken) {
                    dispatch(setToken(newToken));
                }

                return newToken;
            } catch (error) {
                // Return current token and let 401 handler deal with it
                return currentToken;
            }
        }

        // Guest user flow
        const refreshGuestToken = options?.fetchLatestGuestToken;
        if (!refreshGuestToken) {
            console.warn('Cannot refresh guest token: fetchLatestGuestToken function not provided');
            return currentToken;
        }

        try {
            const newGuestToken = await refreshGuestToken();
            if (!newGuestToken) {
                console.warn('Guest token refresh did not return a token, using existing token');
                return currentToken;
            }

            // For guest users we assume fetchLatestGuestToken handles any state updates.
            return newGuestToken;
        } catch (error) {
            console.error('Failed to refresh guest chat token:', error);
            return currentToken;
        }
    }

    // Token is still valid
    return currentToken;
};
